import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PosterPlaceholder from './PosterPlaceholder';
import SiteHeader from './SiteHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Friendly labels + ordering for the radar sections returned by the backend.
const SECTIONS = [
  { key: 'recently_announced', label: 'Recently Announced' },
  { key: 'recently_greenlit', label: 'Recently Greenlit' },
  { key: 'now_filming', label: 'Now Filming' },
  { key: 'recently_updated', label: 'Recently Updated' },
];

// Status -> badge colour. Derived statuses come from the event stream.
const STATUS_STYLES = {
  Announced: 'bg-blue-100 text-blue-700',
  'In Development': 'bg-indigo-100 text-indigo-700',
  Greenlit: 'bg-green-100 text-green-700',
  'Pre-Production': 'bg-amber-100 text-amber-700',
  Filming: 'bg-orange-100 text-orange-700',
  'Post Production': 'bg-purple-100 text-purple-700',
  Released: 'bg-gray-200 text-gray-700',
  Cancelled: 'bg-red-100 text-red-700',
};

export function StatusBadge({ status }) {
  if (!status) return null;
  const cls = STATUS_STYLES[status] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

function ProjectCard({ project }) {
  return (
    <Link to={`/insider/project/${project.id}`} className="flex-shrink-0 w-32 group">
      <div className="w-32 h-48 rounded-xl overflow-hidden bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all duration-200">
        {project.poster_url ? (
          <img
            src={project.poster_url}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <PosterPlaceholder />
        )}
      </div>
      <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight min-h-[1.875rem] mb-1">{project.title}</p>
      <StatusBadge status={project.status} />
    </Link>
  );
}

function RadarRow({ label, items, isLoading }) {
  return (
    <div className="py-8 px-4 border-t border-gray-100">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{label}</h2>
        {isLoading ? (
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-32 h-48 rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {items.map((p) => <ProjectCard key={p.id} project={p} />)}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Nothing here yet.</p>
        )}
      </div>
    </div>
  );
}

// Games carry landscape article art (og:image), not portrait covers like film/TV.
// A wider 16:9 card lets that art sit uniformly instead of being hard-cropped by
// the portrait poster box, so the row lines up cleanly.
function GameCard({ project }) {
  return (
    // The game's own public page, not the gated project view.
    <Link to={`/game/${project.id}`} className="flex-shrink-0 w-56 group">
      <div className="w-56 aspect-video rounded-xl overflow-hidden bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all duration-200">
        {project.poster_url ? (
          <img
            src={project.poster_url}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <PosterPlaceholder />
        )}
      </div>
      <p className="text-xs font-medium text-gray-800 line-clamp-2 leading-tight min-h-[1.875rem] mb-1">{project.title}</p>
      <StatusBadge status={project.status} />
    </Link>
  );
}

// Same section chrome as RadarRow, but renders the landscape GameCard.
function GamesRow({ label, items, isLoading }) {
  return (
    <div className="py-8 px-4 border-t border-gray-100">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{label}</h2>
        {isLoading ? (
          <div className="flex gap-3 overflow-hidden">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-56 aspect-video rounded-xl bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {items.map((p) => <GameCard key={p.id} project={p} />)}
          </div>
        ) : (
          <p className="text-sm text-gray-400">Nothing here yet.</p>
        )}
      </div>
    </div>
  );
}

export default function Insider() {
  const { token } = useAuth();
  const [sections, setSections] = useState({});
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRadar = useCallback(async () => {
    setIsLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const [radarResp, gamesResp] = await Promise.all([
        fetch(`${API_BASE}/api/insider/radar`, { headers }),
        fetch(`${API_BASE}/api/insider/games`, { headers }),
      ]);
      if (!radarResp.ok) throw new Error(`Request failed (${radarResp.status})`);
      const data = await radarResp.json();
      setSections(data.sections || {});
      // Games are a secondary row; a failure there shouldn't blank the page.
      setGames(gamesResp.ok ? (await gamesResp.json()).games || [] : []);
      setError(null);
    } catch (e) {
      setError('Could not load the development radar.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { loadRadar(); }, [loadRadar]);

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      <div className="max-w-6xl mx-auto px-4 pt-10 pb-2">
        <h2 className="text-2xl md:text-3xl font-light text-gray-900">Discover what's being made before everyone else.</h2>
        <p className="text-sm text-gray-500 mt-2">Film, TV &amp; games in development, tracked as a live timeline of events.</p>
      </div>

      {error && (
        <div className="max-w-6xl mx-auto px-4 py-3 text-sm text-red-600">{error}</div>
      )}

      {SECTIONS.map((s) => (
        <RadarRow
          key={s.key}
          label={s.label}
          items={sections[s.key] || []}
          isLoading={isLoading}
        />
      ))}

      {/* Games in development — moved here from the home page. Landscape cards
          because game art is article-style og:images, not portrait covers. */}
      <GamesRow label="Games on the Radar" items={games} isLoading={isLoading} />
    </div>
  );
}
